import React, { useCallback, useContext, useMemo } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import { NumericMetricInfo } from '@moderepo/mode-apis';
import { BaseCompAction, capitalizeAll, getLanguageByCode, XYChart, XYChartType, TimeSeriesChartPanel, TimeSeriesDataTable, BaseInfoCompProps,
    ChartSettings, XYChartProps, BaseCompSubAction, GenericMessageBox, TimeSeriesTableCompField } from '@moderepo/mode-app-components';
import { Agent, SortOrder } from '@moderepo/mode-internal-apis';
import { DataIntervalDashboardWidgetSettings, DataIntervalViewType } from './dashboardTypes';
import { GanttChart } from '../time-series/GanttChart';
import { useConvertMultiSeriesToMultiMetricsDataPoints, useMergeMetricMultiSeriesDataPoints } from '../time-series/tsUtils';
import { DEFAULT_DECIMAL_PLACES, DEFAULT_LANGUAGE, NO_VALUE, SUPPORTED_LANGUAGES } from '../../utils/constants';
import { createChartExportSettings, getPreferredLanguage } from '../../utils/utils';
import { TSDashboardWidgetRequiredViewData } from './dashboardLogic';
import { useRitzTranslation } from '../../utils/customHooks';



const useStyle = makeStyles((theme: Theme) => {
    return {
        widget: {
            width    : '100%',
            height   : '100%',
            minHeight: 'auto',
        },

        widgetViewContent: {
            width  : '100%',
            height : '100%',
            padding: theme.spacing(2),
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
        },
    };
}, { name: 'DataIntervalDashboardWidgetDataViewContainer', index: 2 });



export interface DataIntervalDashboardWidgetDataViewContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly selectedWidget: DataIntervalDashboardWidgetSettings;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
    } | undefined;

    readonly widgetPrimaryActions?: readonly BaseCompAction[] | undefined;
    readonly widgetSecondaryActions?: readonly (BaseCompAction | BaseCompSubAction)[] | undefined;
    readonly metricsData?: TSDashboardWidgetRequiredViewData | undefined;
}



/**
 * This is the component for displaying the data for the DataIntervalDashboardWidget.
 */
export const DataIntervalDashboardWidgetDataViewContainer: React.FC<DataIntervalDashboardWidgetDataViewContainerProps> = (
    props: DataIntervalDashboardWidgetDataViewContainerProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const { state:uiState } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const selectedLanguage = getLanguageByCode(SUPPORTED_LANGUAGES, selectLanguage(uiState)) || DEFAULT_LANGUAGE;
    const currentLanguage = loggedInAgent ? getPreferredLanguage(project, loggedInAgent, DEFAULT_LANGUAGE) : selectedLanguage;
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    // Merge both numericMetrics and tagMetrics because we need to show them both in 1 table
    const mergedMultiSeriesDataPoints = useMergeMetricMultiSeriesDataPoints<number | string>(
        props.selectedWidget.viewSettings.viewType === DataIntervalViewType.TABULAR ? props.metricsData?.numericMetricsData : undefined,
        props.selectedWidget.viewSettings.viewType === DataIntervalViewType.TABULAR ? props.metricsData?.tagMetricsData : undefined,
    );

    // The TimeSeriesDataTable only work with MultiMetricsDataPoint data array so we need to convert the "mergedMultiSeriesDataPoints" array
    // to "MultiMetricsDataPoint" array
    const multiMetricsDataPoints = useConvertMultiSeriesToMultiMetricsDataPoints(mergedMultiSeriesDataPoints);

    const sortedMultiMetricsDataPoints = useMemo(() => {
        if (props.selectedWidget.viewSettings.sortOrder !== undefined && multiMetricsDataPoints) {

            // If sort order is configured, sort the dataPoints by timestamp
            const isDesc = props.selectedWidget.viewSettings.sortOrder === SortOrder.DESC;
            return [...multiMetricsDataPoints].sort((dataPoint1, dataPoints2) => {
                const result = dataPoint1.dataPointTimestamp - dataPoints2.dataPointTimestamp;
                return result * (isDesc ? -1 : 1);
            });
        }

        // SortOrder is not configured, return the same array
        return multiMetricsDataPoints;
    }, [multiMetricsDataPoints, props.selectedWidget.viewSettings.sortOrder]);

    /**
     * Create the export options for the Chart and cache it. We will pass this to the Chart settings later
     */
    const chartExportSettings = useMemo((): ChartSettings['exportSettings'] => {
        return createChartExportSettings(trans);
    }, [trans]);



    /**
     * Create tabular view
     */
    const createTableViewComponent = useCallback((data: TSDashboardWidgetRequiredViewData) => {
        return (
            <TimeSeriesDataTable
                className={clsx(classes.widget, props.className, props.infoCompProps?.className, classes.widget)}
                title={props.selectedWidget.title}
                icon={props.selectedWidget.icon}
                listData={sortedMultiMetricsDataPoints || []}

                // Dynamically add the columns to the table based on the list of selected metrics.
                fieldsSettings={{
                    fields: {
                        date: {
                            dataItemProp: 'date',
                            label       : capitalizeAll(trans('label.date')),
                        },
                        ...([...data.numericMetrics || [], ...data.tagMetrics || []]).reduce((
                            result: {[key: string]: TimeSeriesTableCompField},
                            metric,
                        ): {[key: string]: TimeSeriesTableCompField} => {
                            const metricName = metric.name;
                            const { unit } = metric as NumericMetricInfo;
                            const decimalPlaces = props.selectedWidget.dataSource.metricsMetadata?.[metricName]?.decimalPlaces
                            ?? DEFAULT_DECIMAL_PLACES;

                            return {
                                ...result,
                                [metricName]: {
                                    dataItemProp: metricName,
                                    label       : metric.displayName || metricName,
                                    unit,
                                    decimalPlaces,
                                },
                            };
                        }, {}),
                    },
                }}
                decimalPlaces={DEFAULT_DECIMAL_PLACES}
                locale={currentLanguage.locale2}
                dateTimeFormat={{
                    year                  : 'numeric',
                    month                 : 'short',
                    day                   : '2-digit',
                    hour                  : '2-digit',
                    minute                : '2-digit',
                    second                : '2-digit',
                    fractionalSecondDigits: 3,
                    hour12                : false,
                }}
                labels={{
                    emptyValues: NO_VALUE,
                }}
                customPrimaryActions={props.widgetPrimaryActions}
                customSecondaryActions={props.widgetSecondaryActions}
                showCustomActionOnHover
                noDataMessageComp={(
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                )}
                emptyDataMessageComp={(
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                )}
            />
        );
    }, [classes.widget, props.className, props.infoCompProps?.className, props.selectedWidget.title, props.selectedWidget.icon,
        props.selectedWidget.dataSource.metricsMetadata, props.widgetPrimaryActions, props.widgetSecondaryActions, sortedMultiMetricsDataPoints,
        trans, currentLanguage.locale2]);


    /**
     * Create CHART view component
     */
    const createChartViewComponent = useCallback((data: TSDashboardWidgetRequiredViewData): React.ReactNode => {

        const xyChartType = (() => {
            if (props.selectedWidget.viewSettings.viewType === DataIntervalViewType.BAR_CHART) {
                return XYChartType.BAR;
            }
            if (props.selectedWidget.viewSettings.viewType === DataIntervalViewType.AREA_CHART) {
                return XYChartType.AREA;
            }

            return XYChartType.LINE;
        })();

        /**
         * Convert metricsMetadata to yAxesRange e.g.
         * From:
         *      {
         *          temperature: {yAxisMin: 5, yAxisMax: 10},
         *          pressure: {yAxisMin: -5, yAxisMax: 5}
         *      }
         *
         * To:
         *      {
         *          temperature: {min: 5, max: 10},
         *          pressure: {min: -5, max: 5}
         *      }
         */
        const yAxesRange = props.selectedWidget.viewSettings.metricsMetadata !== undefined
            ? Object.keys(props.selectedWidget.viewSettings.metricsMetadata).reduce((result, metricName): XYChartProps['yAxesRange'] => {
                const metricMetadata = props.selectedWidget.viewSettings.metricsMetadata?.[metricName];
                if (props.metricsData) {
                    return {
                        ...result,
                        [metricName]: {
                            min: metricMetadata?.yAxisMin, max: metricMetadata?.yAxisMax,
                        },
                    };
                }
                return result;
            }, {} as XYChartProps['yAxesRange'])
            : undefined;

        return (
            <TimeSeriesChartPanel
                className={clsx(classes.widget, props.className, props.infoCompProps?.className, classes.widget)}
                title={props.selectedWidget.title}
                icon={props.selectedWidget.icon}
                customPrimaryActions={props.widgetPrimaryActions}
                customSecondaryActions={props.widgetSecondaryActions}
                showCustomActionOnHover
                seriesCount={data.numericMetrics?.length || data.tagMetrics?.length || 0}
                dataCount={data.numericMetricsData?.length || data.tagMetricsData?.length || 0}
                noDataMessageComp={(
                    <GenericMessageBox
                        className={classes.noDataMessage}
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                )}
            >
                {(props.selectedWidget.viewSettings.viewType === DataIntervalViewType.LINE_CHART
                            || props.selectedWidget.viewSettings.viewType === DataIntervalViewType.BAR_CHART
                            || props.selectedWidget.viewSettings.viewType === DataIntervalViewType.AREA_CHART
                ) && (
                    <XYChart
                        className={classes.widgetViewContent}
                        chartId={`widget-${props.selectedWidget.id}`}
                        chartType={xyChartType}
                        dataPoints={data.numericMetricsData || []}
                        displayMetrics={data.numericMetrics || []}
                        showBullets={props.selectedWidget.viewSettings.showBullets ?? false}
                        showYAxes={props.selectedWidget.viewSettings.showYAxes ?? true}
                        showLegends={props.selectedWidget.viewSettings.showLegends ?? false}
                        locale={currentLanguage.locale2}
                        exportSettings={chartExportSettings}
                        yAxesRange={yAxesRange}
                    />
                )}
                {props.selectedWidget.viewSettings.viewType === DataIntervalViewType.GANTT_CHART && (
                    <GanttChart
                        className={classes.widgetViewContent}
                        chartId={`widget-${props.selectedWidget.id}`}
                        dataPoints={data.tagMetricsData || []}
                        displayMetrics={data.tagMetrics || []}
                        dateRange={data.dataRange}
                        locale={currentLanguage.locale2}
                    />
                )}
            </TimeSeriesChartPanel>
        );
    }, [props.selectedWidget.viewSettings.metricsMetadata, props.selectedWidget.viewSettings.viewType, props.selectedWidget.viewSettings.showBullets,
        props.selectedWidget.viewSettings.showYAxes, props.selectedWidget.viewSettings.showLegends, props.selectedWidget.title,
        props.selectedWidget.icon, props.selectedWidget.id, props.className, props.infoCompProps, props.widgetPrimaryActions,
        props.widgetSecondaryActions, props.metricsData, trans, currentLanguage.locale2, chartExportSettings, classes]);



    /**
     * Create the view component e.g. LineChart, BarChart, GanttChart, Table, etc... based on the widget settings. This function will not
     * create a view, it will call the appropriate helper function to create specific view based on the widget settings.
     */
    const createViewComponent = useCallback((data: TSDashboardWidgetRequiredViewData): React.ReactNode => {
        switch (props.selectedWidget.viewSettings.viewType) {
            case DataIntervalViewType.LINE_CHART:
            case DataIntervalViewType.BAR_CHART:
            case DataIntervalViewType.AREA_CHART:
            case DataIntervalViewType.GANTT_CHART:
                return createChartViewComponent(data);
            case DataIntervalViewType.TABULAR:
                return createTableViewComponent(data);
            default:
                return (
                    <div>Unsupported View Type {`${props.selectedWidget.viewSettings.viewType}`}</div>
                );
        }
    }, [createChartViewComponent, createTableViewComponent, props.selectedWidget.viewSettings.viewType]);


    return (
        <>
            {props.metricsData && createViewComponent(props.metricsData)}
        </>
    );
};
